import { Box, CircularProgress, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { useSearchParams } from "react-router-dom";
import { getAPI, getAPIAsync } from "../api/apiCalls";
import Provider from "../api/provider";
import { ConversationStatus, EntityType, icon, inIframe, isNullOrEmpty, MAX_RECORD, WEBSITE_PATH, windowPostMessage } from "../common/common";
import { highlightSearchData } from "../common/commonJS";
import { resource } from "../common/resource";
import ListCards, { SetUserData } from "../components/cardsList";
import { CreateHeader } from "../components/headers";
import InboxSkeleton from "../components/skeletonLoaders/inboxSkeleton";
import { TextFieldOutlinedComponent } from "../components/textfield";
import { UserDetailsContext } from "../contexts/entityDetailsContext";
import { FollowingAccountsResponse, FollowingEntityInfo } from "../models/inbox";
import { ChatHistory } from "../models/messaging";
import "../styles/inbox.css";

const NewChat = () => {
  const { userDetails, setUserDetails } = useContext<any>(UserDetailsContext);
  const [loader, setLoader] = useState(true);
  const [isAjax, setIsAjax] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(0);
  const [totalFollowingCount, setTotalFollowingCount] = useState<number>(0);
  const [recent, setRecent] = useState<ChatHistory[]>([]);
  const [following, setFollowing] = useState<FollowingEntityInfo[]>([]);
  const [delayDebounceFn, setDelayDebounceFn] = useState<any>();
  const [loaderPage, setLoaderPage] = useState(true);
  const [searchParam] = useSearchParams();
  const usercodenum: any = useRef();
  //#region useEffect
  useEffect(() => {
    if (!inIframe()) {
      window.location.href = WEBSITE_PATH;
      return;
    }
    usercodenum.current = encodeURIComponent(searchParam.get("userId"));
    window.addEventListener("message", handleWindowMessageReceived);

    return () => {
      window.removeEventListener("message", handleWindowMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNullOrEmpty(userDetails)) {
      windowPostMessage({ type: "getuserdetails" }, WEBSITE_PATH);
    } else {
      getUserInboxConversation(userDetails.ucnum);
      getUserFollowingList(searchText, pageNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  useEffect(() => {
    if (!isNullOrEmpty(userDetails)) {
      if (delayDebounceFn !== null) {
        clearTimeout(delayDebounceFn);
      }
      const debounceFn = setTimeout(async () => {
        await getUserFollowingList(searchText, pageNo);
      }, 200);
      setDelayDebounceFn(debounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, pageNo]);
  //#endregion

  //#region Api calls
  const getUserFollowingList = async (searchTextVal: string, pageNoVal: number) => {
    getAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.UserFollowing,
      {
        ucnum: userDetails.ucnum,
        isAllPostsCount: totalFollowingCount === 0,
        searchText: searchTextVal,
        pageSize: MAX_RECORD,
        pageNo: pageNoVal,
      },
      (response: any) => {
        setLoaderPage(false);
        const data: FollowingAccountsResponse[] = response.data;
        let followingListCount: number = 0;
        if (data) {
          if (data[0]?.totalFollowers) {
            setTotalFollowingCount(data[0]?.totalFollowers);
            followingListCount = data[0]?.totalFollowers;
          } else {
            followingListCount = totalFollowingCount;
          }

          if (data[0]?.followingEntityInfo) {
            const followingData = pageNoVal === 0 ? [] : following;
            const existingData = [...followingData, ...data[0].followingEntityInfo];
            if (searchTextVal !== "") {
              existingData.forEach((el) => {
                el.nameTemp = highlightSearchData(el.name, searchText);
              });
            } else {
              existingData.forEach((el) => {
                el.nameTemp = el.name;
              });
            }

            setFollowing(existingData);
            if (existingData.length >= followingListCount) {
              setLoaderAndAjax(false, false);
              return;
            }
          }
          setLoaderAndAjax(false, true);
        } else {
          setLoaderAndAjax(false, false);
        }
      },
      (e: any) => {
        setLoaderAndAjax(false, false);
        setLoaderPage(false);
        isNullOrEmpty(e.data) && windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };

  const getUserInboxConversation = (usercodenum: string) => {
    getAPI(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.InboxHistory,
      {
        ucnum: usercodenum,
        pageSize: MAX_RECORD,
        pageNo: 0,
        entityType: EntityType.USER,
        status: ConversationStatus.APPROVED,
      },
      (response: any) => {
        let data = response.data;
        if (data) {
          data = response.data.conversationsResponse;
          setRecent(data);
        }
      },
      (e: any) => {
        isNullOrEmpty(e.data) && windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };
  //#endregion

  //#region Function calls

  const handleWindowMessageReceived = (event: any) => {
    if (event.origin && event.origin.includes(WEBSITE_PATH.slice(0, -1))) {
      const data = event.data;
      if (data.type === "userdetails") {
        SetUserData(usercodenum.current, data.userDetails, searchParam.get("sessionId"), setUserDetails);
      }
    }
  };

  const onScrollEvent = (e: any) => {
    const $eTarget = e.currentTarget;
    if (!isAjax || !($eTarget.scrollHeight - $eTarget.scrollTop - 10 <= $eTarget.clientHeight)) {
      return;
    }
    setLoaderAndAjax(true, false);
    setPageNo(pageNo + MAX_RECORD);
  };

  const setLoaderAndAjax = (loader: boolean, ajax: boolean) => {
    setLoader(loader);
    setIsAjax(ajax);
  };

  const onTextfieldChange = (e: any) => {
    setPageNo(0);
    setFollowing([]);
    setSearchText(e.currentTarget.value);
  };

  const onTrailIconClick = () => {
    setSearchText("");
  };
  //#endregion

  return (
    <Box className="inboxWindow flex-column pad-top-gutter pad-top-bottom">
      <CreateHeader title="New Chat" isTextBox={false} />
      <Box className="searchBox pad-l-r-gutter">
        <TextFieldOutlinedComponent placeholder="Search" value={searchText} onChange={onTextfieldChange} trailIcon={icon({ iconName: "search", iconColor: "icon-spanish-gray" })} />
      </Box>
      {loaderPage ? (
        <InboxSkeleton noTop={true} />
      ) : (
        <Box className="genericContentContainer pad-l-r-gutter mar-t-24" onScroll={onScrollEvent}>
          {recent.length > 0 && searchText === "" && (
            <Box className="flex-column max-width-100">
              <Box className="flex-row flex-align-center mar-b-8 ht-48" sx={{ paddingLeft: isMobileOnly ? "24px" : "28px" }}>
                <Typography variant="subtitle1" className="text-primary">
                  Recent
                </Typography>
              </Box>
              <ListCards data={recent} type="newchatrecent" secondaryActionWidth="32px"/>
            </Box>
          )}
          {following.length > 0 && (
            <Box className={`flex-column w-per-100 ${recent.length > 0 && searchText === "" ? "mar-t-24" : ""}`}>
              <Box className="flex-row flex-align-center mar-b-8 ht-48" sx={{ paddingLeft: isMobileOnly ? "24px" : "28px" }}>
                <Typography variant="subtitle1" className="text-primary">
                  Following
                </Typography>
              </Box>
              <ListCards data={following} type="newchat" secondaryActionWidth="32px"/>
              {loader && (
                <Box className="flex-center ht-64" sx={{ m: 2 }}>
                  <CircularProgress className="box-24" />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default NewChat;
